import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth';
import { useLocker } from '../features/locker';
import { useToast } from '../features/toast';
import { auth } from '../services/firebase';
import FreeShippingProgress from '../components/FreeShippingProgress';
import {
  aggregateLockerShipments,
} from '../utils/shippingUtils';
import Seo from '../shared/Seo';

const FALLBACK_IMAGE = '/assets/ponpon-logo.svg';

const LockerPage = () => {
  const { user } = useAuth();
  const { startShippingFlow, openLockerFullModal } = useLocker();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isAuthenticated = Boolean(user);

  const lockerItems = useMemo(() => {
    const source = Array.isArray(user?.locker)
      ? user.locker
      : Array.isArray(user?.history)
      ? user.history
      : [];
    if (!Array.isArray(source)) {
      return [];
    }
    return [...source]
      .sort((a, b) => {
        const timeA = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
      });
  }, [user?.locker, user?.history]);
  const pendingPrize = user?.pendingPrize || null;
  
  // CHANGED: combine lockerItems with pendingPrize for display, with pendingPrize at the top
  const displayItems = useMemo(() => {
    const items = [];
    if (pendingPrize) {
      items.push({
        ...pendingPrize,
        isPending: true,
        timestamp: pendingPrize?.timestamp || new Date().toISOString(),
      });
    }
    items.push(...lockerItems);
    return items;
  }, [pendingPrize, lockerItems]);
  const canShip = lockerItems.length > 0;
  const currentCount = lockerItems.length;
  const handleShipClick = () => {
    if (canShip) {
      startShippingFlow('manual');
    }
  };
  const handleOpenLockerFullModal = () => {
    // CHANGED: call openLockerFullModal to trigger LockerFullModal display
    openLockerFullModal();
  };
  const shippingGroups = useMemo(
    () => aggregateLockerShipments(user?.lockerShipments),
    [user?.lockerShipments],
  );
  const [expandedShipments, setExpandedShipments] = useState(() => new Set());
  const toggleShipmentExpansion = (groupId) => {
    setExpandedShipments((current) => {
      const next = new Set(current);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // CHANGED: keep page visible; use toast to block actions when needed

  return (
    <div className="flex flex-col gap-8">
      <Seo
        title="Locker | PonPon!"
        description="Use the PonPon Locker to store every prize, expand capacity when needed, review spin history, and trigger a free shipment once your slots are full."
        canonical="https://ponpon.app/locker"
        noindex={true}
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-ponpon-navy sm:text-4xl">
            Your Locker
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 rounded-full border border-ponpon-navy/15 bg-white px-5 py-2 text-sm font-semibold text-ponpon-navy/70 transition hover:-translate-y-0.5 hover:border-ponpon-coral/40 hover:text-ponpon-coral focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/50"
          >
            <span aria-hidden="true">←</span>
            Back to homepage
          </button>
          {/* CHANGED: add entry point to Locker Showcase builder */}
          <button
            type="button"
            onClick={() => navigate('/locker/showcase')}
            className="inline-flex items-center gap-2 rounded-full border border-ponpon-navy/15 bg-white px-5 py-2 text-sm font-semibold text-ponpon-navy/80 transition hover:-translate-y-0.5 hover:border-ponpon-coral/40 hover:text-ponpon-coral focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/50"
          >
            PonDisplay
          </button>
          {canShip ? (
            <button
              type="button"
              onClick={async () => {
                if (!isAuthenticated) {
                  showToast({ message: 'Sign in to ship your Locker items.', tone: 'info' });
                  return;
                }
                // CHANGED: use Firebase auth.currentUser.emailVerified for consistency (Google users are auto-verified)
                const currentUser = auth.currentUser;
                if (!currentUser) {
                  showToast({ message: 'Sign in to ship your Locker items.', tone: 'info' });
                  return;
                }
                try { await currentUser.reload(); } catch (_) {}
                if (!auth.currentUser?.emailVerified) {
                  showToast({ message: 'Verify your email before shipping your Locker.', tone: 'info' });
                  return;
                }
                handleShipClick();
              }}
              className="inline-flex items-center gap-2 rounded-full bg-ponpon-coral px-6 py-2.5 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60"
            >
              Ship My Locker
            </button>
          ) : null}
        </div>
      </div>

      {isAuthenticated ? (
        <div className="flex w-full flex-col gap-3">
          <p className="flex items-center gap-2 text-lg font-semibold text-ponpon-navy">
            <span>Collect 8</span>
            <img
              src="/assets/yellow_gacha.png"
              alt="Gachapon capsule"
              className="h-6 w-6 object-contain"
              loading="lazy"
              decoding="async"
            />
            <span>to unlock free shipping!</span>
          </p>
          <FreeShippingProgress currentCount={currentCount} />
        </div>
      ) : null}

      {pendingPrize ? (
        <div className="rounded-3xl border border-ponpon-coral/30 bg-ponpon-coral/5 p-4 text-sm text-ponpon-coral">
          Your locker is full.{' '}
          <button
            type="button"
            onClick={handleOpenLockerFullModal}
            className="font-semibold underline underline-offset-2 hover:text-ponpon-navy transition-colors"
          >
            Ship or expand
          </button>{' '}
          to claim your pending prize.
        </div>
      ) : null}

      {displayItems.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {displayItems.map((entry) => {
            const isPending = entry?.isPending || false;
            const prizeName = entry?.prize?.name || entry?.name || 'Mystery prize';
            const prizeImg =
              typeof entry?.prize?.imageUrl === 'string' && entry.prize.imageUrl.trim().length > 0
                ? entry.prize.imageUrl.trim()
                : typeof entry?.imageUrl === 'string' && entry.imageUrl.trim().length > 0
                ? entry.imageUrl.trim()
                : FALLBACK_IMAGE;
            const machineName = entry?.machineName || entry?.prize?.machineName || 'PonPon Machine';
            // CHANGED: Only keep calendar date (no time) for Locker items
            const timestamp = entry?.timestamp ? new Date(entry.timestamp) : null;
            const formattedDate = Number.isFinite(timestamp?.getTime())
              ? new Intl.DateTimeFormat(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                }).format(timestamp)
              : 'Just now';

            return (
              <article
                key={entry?.id || `${entry?.prize?.id || entry?.id || 'pending'}-${entry?.timestamp || 'pending'}`}
                className={`relative flex flex-col gap-4 rounded-3xl border p-5 shadow-sm transition ${
                  isPending
                    ? 'border-ponpon-coral/50 bg-gradient-to-br from-ponpon-coral/10 to-white/95 ring-2 ring-ponpon-coral/20'
                    : 'border-white/70 bg-white/95 hover:-translate-y-1 hover:shadow-lg'
                }`}
              >
                {isPending ? (
                  <span className="absolute right-4 top-4 rounded-full bg-ponpon-coral px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white shadow-sm">
                    Pending
                  </span>
                ) : null}
                <div className="flex items-center justify-center rounded-2xl p-5">
                  <img
                    src={prizeImg}
                    alt={prizeName}
                    className="max-h-28 w-auto object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold text-ponpon-navy">{prizeName}</h2>
                  <p className="text-xs font-medium uppercase tracking-wide text-ponpon-navy/55">
                    {machineName}
                  </p>
                  {/* CHANGED: Show only date (no time) in Locker item card */}
                  <p className={`text-xs ${isPending ? 'text-ponpon-coral/70 font-semibold' : 'text-ponpon-navy/60'}`}>
                    {isPending ? 'Waiting for locker space' : formattedDate}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="rounded-3xl border border-dashed border-ponpon-navy/20 bg-white/90 p-10 text-center text-sm text-ponpon-navy/65">
          Spin a machine to start filling your locker. Every prize will appear here.
        </div>
      )}

      {shippingGroups.length ? (
        <section className="flex flex-col gap-4">
          <header>
            <h2 className="text-lg font-semibold text-ponpon-navy">Order Status</h2>
          </header>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-2">
            {shippingGroups.map((group) => {
              // CHANGED: Simplified shipped card — remove fee/status text
              const isExpanded = expandedShipments.has(group.id);
              const displayItems = isExpanded ? group.items : group.items.slice(0, 2);
              const hiddenCount = Math.max(group.items.length - displayItems.length, 0);
              const canCollapse = isExpanded && group.items.length > 2;

              return (
                <article
                  key={group.id}
                  className="flex flex-col gap-4 rounded-3xl border border-white/70 bg-white/90 p-5 shadow-sm ring-1 ring-ponpon-navy/5"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-2">
                      <span className="inline-flex items-center rounded-full bg-ponpon-navy/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-ponpon-navy">
                        {/* CHANGED: Badge label from SHIPPING to SHIPPED */}
                        SHIPPED
                      </span>
                      <div className="space-y-1">
                        {group.shipmentId || group.id ? (
                          <p className="text-lg font-semibold text-ponpon-navy">
                            {group.shipmentId || group.id}
                          </p>
                        ) : null}
                        {group.trackingNumber ? (
                          <p className="text-lg font-semibold text-ponpon-navy">
                            Tracking #{group.trackingNumber}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    {/* CHANGED: Only show count when more than 1 item, format as "[n] total" */}
                    {group.itemCount > 1 ? (
                      <p className="self-end text-xs font-semibold uppercase tracking-wide text-ponpon-navy/60 sm:self-auto">
                        {group.itemCount} total
                      </p>
                    ) : null}
                  </div>
                  <div
                    id={`shipment-items-${group.id}`}
                    className="flex flex-wrap gap-3 border-t border-ponpon-navy/10 pt-4"
                  >
                    {displayItems.map((item) => {
                      const prizeName = item?.prize?.name || 'Mystery prize';
                      const prizeImg =
                        typeof item?.prize?.imageUrl === 'string' && item.prize.imageUrl.trim().length > 0
                          ? item.prize.imageUrl.trim()
                          : FALLBACK_IMAGE;

                      return (
                        <div
                          key={item?.id || item?.prize?.id || `${group.id}-${prizeName}`}
                          className="flex items-center gap-2 rounded-2xl border border-ponpon-navy/10 bg-white/75 px-3 py-2"
                        >
                          <img
                            src={prizeImg}
                            alt={prizeName}
                            className="h-10 w-10 rounded-xl object-contain"
                            loading="lazy"
                            decoding="async"
                          />
                          <div className="text-xs">
                            <p className="font-semibold text-ponpon-navy">{prizeName}</p>
                            <p className="text-[10px] uppercase tracking-wide text-ponpon-navy/55">
                              {item?.machineName || item?.prize?.machineName || 'PonPon Machine'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    {hiddenCount && !isExpanded ? (
                      <button
                        type="button"
                        onClick={() => toggleShipmentExpansion(group.id)}
                        className="inline-flex items-center rounded-2xl bg-ponpon-navy/5 px-3 py-2 text-xs font-semibold text-ponpon-navy/70 transition hover:-translate-y-0.5 hover:bg-ponpon-coral/10 hover:text-ponpon-coral"
                        aria-label={`View ${hiddenCount} more items for this shipment`}
                      >
                        +{hiddenCount} more
                      </button>
                    ) : null}
                  </div>
                  {canCollapse ? (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => toggleShipmentExpansion(group.id)}
                        className="text-xs font-semibold uppercase tracking-wide text-ponpon-navy/70 underline-offset-4 hover:text-ponpon-coral hover:underline"
                      >
                        Hide
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default LockerPage;
